import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { getAccessToken } from '../Auth/AuthStore'
import styles from './DiaryDetail.module.css'
import { emotions } from './EmotionSelectModal'
import { AlertModal, ConfirmModal } from '../AlertModal'

const DiaryDetail = ({ diaryId, onNavigateToDiary }) => {
  const [diary, setDiary] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showAlert, setShowAlert] = useState(false)
  const [alertMessage, setAlertMessage] = useState('')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const emotionColors = emotions.reduce((acc, emotion) => {
    acc[emotion.id] = emotion.color
    return acc
  }, {})

  // 감정에 따른 메시지 (DiaryResult와 동일 구조에서 message만 사용)
  const generateAIResponse = (emotion) => {
    const aiResponses = {
      happy: {
        message: '행복한 순간들이 모여 아름다운 하루가 되었네요. 오늘의 기쁨이 내일의 힘이 됩니다!',
      },
      excited: {
        message: '새로운 시작에 대한 설렘이 느껴져요. 기대하는 마음으로 내일을 맞이해보세요!',
      },
      love: {
        message: '사랑하는 마음은 세상을 아름답게 만듭니다. 그 마음을 계속 간직하세요.',
      },
      calm: {
        message: '평온함 속에서 진정한 나를 만날 수 있어요. 이 고요함을 즐겨보세요.',
      },
      anxious: {
        message: '걱정은 내일의 힘을 빼앗아갑니다. 깊은 숨을 쉬고, 한 걸음씩 나아가세요.',
      },
      proud: {
        message: '스스로를 자랑스럽게 여기는 것은 성장의 증거입니다. 당신은 충분히 잘하고 있어요!',
      },
      tired: {
        message: '피곤한 하루였군요. 충분히 쉬면서 자신을 돌봐주세요. 내일은 더 나은 날이 될 거예요.',
      },
      sad: {
        message: '슬픔도 소중한 감정이에요. 이 감정을 느끼고 받아들이는 당신이 강하답니다.',
      },
      lonely: {
        message: '혼자라는 느낌이 들 때, 자신과 깊이 대화할 수 있는 시간이기도 해요.',
      },
      angry: {
        message: '화가 나는 것도 괜찮아요. 감정을 표현하고 천천히 진정해보세요.',
      },
      confused: {
        message: '혼란스러울 때는 한 발짝 물러서서 생각해보세요. 답은 가까이에 있을 거예요.',
      },
      depressed: {
        message: '우울한 마음도 언젠가는 지나갑니다. 작은 것에서 위안을 찾아보세요.',
      }
    }

    return aiResponses[emotion.id] || aiResponses.happy
  }

  const fetchDiaryDetail = async () => {
    try {
      setLoading(true)
      setError(null)

      const token = getAccessToken()
      if (!token) throw new Error('로그인 토큰이 없습니다.')

      const response = await axios.get(
        `${import.meta.env.VITE_API_BASE}/api/diaries/${diaryId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      const diaryData = response.data?.data
      if (!diaryData) {
        setError('일기 정보를 불러올 수 없습니다.')
        return
      }

      // 서버에서 내려주는 keyword(예: "행복")를 프론트 감정 id(예: "happy")로 매핑
      const emotionByLabel = emotions.reduce((acc, emotion) => {
        acc[emotion.label] = emotion
        return acc
      }, {})

      const normalizedKeyword = diaryData.keyword || ''

      // 1순위: 감정 한글 라벨 매칭, 2순위: id 직접 매칭 (백엔드가 영어 enum을 내려줄 경우 대비)
      const matchedEmotion =
        emotionByLabel[normalizedKeyword] ||
        emotions.find((e) => e.id === normalizedKeyword.toLowerCase()) ||
        emotions[0]

      setDiary({
        id: diaryData.diaryId,
        title: diaryData.title,
        content: diaryData.content,
        date: diaryData.writtenAt,
        imageUrl: diaryData.imageUrl,
        emotion: matchedEmotion,
      })
    } catch (err) {
      if (err.response?.status === 403) {
        setError('해당 일기에 접근할 권한이 없습니다.')
      } else {
        setError('일기를 불러오는 중 오류가 발생했습니다.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = () => {
    if (!diary) return
    setShowDeleteConfirm(true)
  }

  const confirmDelete = async () => {
    if (!diary) return

    try {
      const token = getAccessToken()
      if (!token) throw new Error('로그인 토큰이 없습니다.')

      await axios.delete(
        `${import.meta.env.VITE_API_BASE}/api/diaries/${diary.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      setAlertMessage('일기가 삭제되었습니다.')
      setShowAlert(true)
      setShowDeleteConfirm(false)
      setTimeout(() => {
        onNavigateToDiary()
      }, 1000)
    } catch (err) {
      setShowDeleteConfirm(false)
      if (err.response?.status === 403) {
        setAlertMessage('일기에 대한 접근 권한이 없습니다.')
      } else if (err.response?.status === 404) {
        setAlertMessage('일기를 찾을 수 없습니다.')
      } else {
        setAlertMessage('일기 삭제 중 오류가 발생했습니다.')
      }
      setShowAlert(true)
    }
  }

  useEffect(() => {
    if (diaryId) {
      fetchDiaryDetail()
    }
  }, [diaryId])

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.loadingContainer}>
        <p>{error}</p>
        <button className={styles.listButton} onClick={onNavigateToDiary}>
          일기장으로 돌아가기
        </button>
      </div>
    )
  }

  if (!diary) {
    return (
      <div className={styles.loadingContainer}>
        <p>일기 정보를 찾을 수 없습니다.</p>
        <button className={styles.listButton} onClick={onNavigateToDiary}>
          일기장으로 돌아가기
        </button>
      </div>
    )
  }

  const backgroundColor = emotionColors[diary.emotion.id] || '#7c3aed'
  const aiResponse = generateAIResponse(diary.emotion)

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).replace(/\. /g, '. ').slice(0, -1)
  }

  const formatTime = (dateString) => {
    const date = new Date(dateString)
    const hours = date.getHours()
    const minutes = date.getMinutes()
    const period = hours < 12 ? '오전' : '오후'
    const displayHours = hours % 12 || 12
    return `${period} ${displayHours}시 ${minutes}분`
  }

  return (
    <div className={styles.container} style={{ background: backgroundColor }}>
      <div className={styles.content}>
        <div className={styles.dateSection}>
          <div className={styles.dateMain}>{formatDate(diary.date)}</div>
          <div className={styles.emotionTags}>
            <span className={styles.emotionTag}>#{diary.emotion.label}</span>
          </div>
        </div>

        <div className={styles.aiImageSection}>
          <div className={styles.imageWrapper}>
            <img src={diary.imageUrl} alt="AI Generated" className={styles.aiImage} />
          </div>
        </div>

        <div className={styles.messageSection}>
          <div className={styles.aiMessage}>
            {aiResponse.message}
          </div>
        </div>

        <div className={styles.diaryContent}>
          <h3 className={styles.contentTitle}>{diary.title}</h3>
          <p>{diary.content}</p>
        </div>

        <div className={styles.buttonGroup}>
          <button className={styles.deleteButton} onClick={handleDelete}>
            <i className="fas fa-trash-alt"></i>
            <span>삭제하기</span>
          </button>
          <button className={styles.listButton} onClick={onNavigateToDiary}>
            <i className="fas fa-list"></i>
            <span>목록으로</span>
          </button>
        </div>
      </div>

      <AlertModal
        isOpen={showAlert}
        onClose={() => setShowAlert(false)}
        message={alertMessage}
      />

      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDelete}
        title="일기 삭제"
        message="일기를 삭제하시겠습니까?"
        confirmText="삭제"
        cancelText="취소"
        confirmButtonColor="#ef4444"
      />
    </div>
  )
}

export default DiaryDetail
